import { z } from "zod";
import { parse } from "tldts";

export const stages = [
  "started",
  "pain_answered",
  "business_fit",
  "preview_invitation",
  "contact_opened",
  "details_entered",
  "contact_submitted",
  "lead_only_submitted",
  "auth_started",
  "auth_completed",
  "auth_failed",
  "analysis_started",
  "analysis_failed",
  "analysis_completed",
  "results_viewed",
  "exited",
  "finished",
] as const;
export type Stage = (typeof stages)[number];
export type Panel =
  | "journey"
  | "contact"
  | "exit"
  | "details"
  | "authenticate"
  | "analyzing"
  | "interrupted"
  | "ready"
  | "failed"
  | "not_relevant"
  | "lead_complete";
export const snapshotSchema = z.object({
  version: z.literal(1),
  journeyId: z.uuid(),
  updatedAt: z.number(),
  revision: z.number().int().nonnegative(),
  step: z.number().int().min(0).max(3),
  pain: z.number().int().min(0).max(3).nullable(),
  fit: z.number().int().min(0).max(3).nullable(),
  fitResult: z.boolean(),
  panel: z.enum([
    "journey",
    "contact",
    "exit",
    "details",
    "authenticate",
    "analyzing",
    "interrupted",
    "ready",
    "failed",
    "not_relevant",
    "lead_complete",
  ]),
  lastStage: z.enum(stages),
  furthestStage: z.enum(stages),
  contactSubmitted: z.boolean(),
  previewSaved: z.boolean(),
  leadOnlySubmitted: z.boolean().default(false),
  milestones: z.record(z.string(), z.number()),
});
export type Snapshot = z.infer<typeof snapshotSchema>;
export type ContactDraft = {
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
};
export type PreviewDraft = { websiteUrl: string; name: string; phone: string };
export const emptyContact: ContactDraft = {
  name: "",
  email: "",
  phone: "",
  business: "",
  message: "",
};
export function normalizeWebsite(value: string): string | null {
  try {
    if (!value.trim() || Array.from(value.trim()).length > 2048) return null;
    const url = new URL(
      /^https?:\/\//i.test(value.trim()) ? value.trim() : `https://${value.trim()}`,
    );
    const domain = parse(url.hostname, { allowPrivateDomains: true });
    if (
      !["http:", "https:"].includes(url.protocol) ||
      url.username ||
      url.password ||
      !domain.domain ||
      !(domain.isIcann || domain.isPrivate) ||
      domain.isIp ||
      url.hostname.endsWith(".localhost") ||
      url.hostname.endsWith(".local")
    )
      return null;
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
}
export function validPhone(value: string) {
  return (
    value.length <= 40 &&
    /^[+()\d\s.-]+$/.test(value.trim()) &&
    value.replace(/\D/g, "").length >= 7 &&
    value.replace(/\D/g, "").length <= 15
  );
}
export function newSnapshot(): Snapshot {
  return {
    version: 1,
    journeyId: crypto.randomUUID(),
    updatedAt: Date.now(),
    revision: 0,
    step: 0,
    pain: null,
    fit: null,
    fitResult: false,
    panel: "journey",
    lastStage: "started",
    furthestStage: "started",
    milestones: { started: Date.now() },
    contactSubmitted: false,
    previewSaved: false,
    leadOnlySubmitted: false,
  };
}
export function advance(state: Snapshot, stage: Stage, patch: Partial<Snapshot> = {}): Snapshot {
  // Exit/contact are side branches, not higher conversion milestones.
  const rank = (value: Stage) =>
    ["contact_opened", "contact_submitted", "lead_only_submitted", "exited", "finished"].includes(
      value,
    )
      ? -1
      : stages.indexOf(value);
  return {
    ...state,
    ...patch,
    revision: state.revision + 1,
    updatedAt: Date.now(),
    lastStage: stage,
    furthestStage: rank(stage) > rank(state.furthestStage) ? stage : state.furthestStage,
    milestones: { ...state.milestones, [stage]: state.milestones[stage] ?? Date.now() },
  };
}
